import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Card, CardContent, Chip, CircularProgress, FormControlLabel,
  MenuItem, Stack, Switch, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import {
  AllocationTargetsService,
  type AllocationTarget,
  type TradeSuggestion,
} from '../../services/allocation-targets-service';
import { baseMoney } from '../../stores/currency-store';

interface Props {
  targets: AllocationTarget[];
}

/**
 * Suggested category-level trades toward a target.
 *
 * Two modes, because taxes make them different problems: full rebalance
 * sells overweights to fund underweights; buy-only places NEW cash against
 * the worst gaps and never sells, so nothing is realised.
 */
export const RebalanceTab: React.FC<Props> = ({ targets }) => {
  const [targetId, setTargetId] = useState<string>(targets[0]?.id ?? '');
  const [buyOnly, setBuyOnly] = useState(false);
  const [minTrade, setMinTrade] = useState('100');
  const [cash, setCash] = useState('1000');
  const [trades, setTrades] = useState<TradeSuggestion[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!targets.some((t) => t.id === targetId)) {
      setTargetId(targets[0]?.id ?? '');
    }
  }, [targets, targetId]);

  const load = useCallback(async () => {
    if (!targetId) { setTrades(null); return; }
    if (buyOnly && !(Number(cash) > 0)) return;
    setLoading(true);
    setError(null);
    try {
      const r = await AllocationTargetsService.rebalance(targetId, {
        minTrade: minTrade || undefined,
        buyOnly,
        cash: buyOnly ? cash : undefined,
      });
      setTrades(r.trades);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to compute suggestions');
    } finally {
      setLoading(false);
    }
  }, [targetId, buyOnly, minTrade, cash]);

  // Debounced: threshold and cash are typed digit by digit, and firing a
  // request per keystroke would flood the API for intermediate values.
  useEffect(() => {
    const t = window.setTimeout(() => { void load(); }, 350);
    return () => window.clearTimeout(t);
  }, [load]);

  if (targets.length === 0) {
    return (
      <Alert severity="info">
        Create a target first — suggestions are measured against it.
      </Alert>
    );
  }

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <TextField
          select size="small" label="Target" value={targetId}
          onChange={(e) => setTargetId(e.target.value)} sx={{ minWidth: 220 }}
        >
          {targets.map((t) => (
            <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small" label="Min trade ($)" value={minTrade} sx={{ width: 130 }}
          onChange={(e) => setMinTrade(e.target.value)}
        />
        <FormControlLabel
          control={<Switch checked={buyOnly} onChange={(e) => setBuyOnly(e.target.checked)} />}
          label="Buy-only (new cash)"
        />
        {buyOnly && (
          <TextField
            size="small" label="New cash ($)" value={cash} sx={{ width: 130 }}
            onChange={(e) => setCash(e.target.value)}
          />
        )}
        {loading && <CircularProgress size={20} />}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {trades && (
        <Card>
          <CardContent sx={{ pb: 1 }}>
            {trades.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 2 }}>
                Nothing to do — every gap is smaller than the minimum trade.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Action</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Candidate holdings</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {trades.map((t, i) => (
                      <TableRow key={`${t.action}-${t.category}-${i}`} hover>
                        <TableCell>
                          <Chip
                            size="small"
                            label={t.action.toUpperCase()}
                            color={t.action === 'sell' ? 'warning' : 'success'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={650}>{t.category}</Typography>
                        </TableCell>
                        <TableCell align="right" className="fw-num">
                          {baseMoney(t.amount)}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {t.symbols?.length ? t.symbols.join(', ') : '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', my: 1 }}>
              These are the arithmetic gaps between your written target and your
              holdings, expressed as trades — not a recommendation to make them.
              Selling in taxable accounts realises gains; the buy-only mode
              avoids that entirely.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
